"use client";

import { useState, useRef, useEffect, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ChevronsUpDown, Check, UserRound } from "lucide-react";
import { setActingUserAction } from "@/app/actions";

export default function IdentitySwitcher({
  members,
  actingEmail,
}: {
  members: Array<{ id: number; name: string | null; email: string; role: string }>;
  actingEmail?: string | null;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [pending, start] = useTransition();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const active = members.find((m) => m.email === actingEmail);

  function choose(email: string) {
    setOpen(false);
    start(async () => {
      await setActingUserAction(email);
      router.refresh();
    });
  }

  if (members.length === 0) {
    return (
      <Link
        href="/permissions"
        className="block rounded-[6px] px-3 py-2 text-[12px] font-light"
        style={{ color: "var(--c-on-dark-soft)", boxShadow: "inset 0 0 0 1px var(--c-on-dark-hairline)" }}
      >
        <span className="flex items-center gap-2">
          <UserRound size={13} /> add your team to act
        </span>
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex w-full items-center gap-2 rounded-[6px] px-3 py-2 text-left transition-colors duration-200"
        style={{
          background: "transparent",
          color: active ? "var(--c-white)" : "var(--c-on-dark-soft)",
          boxShadow: "inset 0 0 0 1px var(--c-on-dark-hairline)",
          opacity: pending ? 0.6 : 1,
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <UserRound size={13} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate text-[12px] font-medium">
          {active ? (active.name ?? active.email) : "acting as…"}
        </span>
        {active && (
          <span className="mono shrink-0 text-[10px]" style={{ color: "var(--c-on-dark-soft)" }}>
            {active.role}
          </span>
        )}
        <ChevronsUpDown size={13} className="shrink-0" style={{ opacity: 0.6 }} />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-[6px] py-1"
          style={{ background: "var(--c-white)" }}
        >
          <div className="eyebrow px-3 pb-1 pt-2" style={{ color: "var(--c-ink-soft)" }}>
            acting as
          </div>
          {members.map((m) => (
            <button
              key={m.id}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors duration-150 hover:bg-[var(--c-cream)]"
              style={{ color: "var(--c-charcoal)", fontWeight: m.email === actingEmail ? 500 : 300 }}
              onClick={() => choose(m.email)}
            >
              <span className="min-w-0 flex-1 truncate">
                {m.name ?? m.email}
                <span className="ml-1 text-[11px]" style={{ color: "var(--c-ink-soft)" }}>
                  · {m.role}
                </span>
              </span>
              {m.email === actingEmail && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
